import React, {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getProductById } from "../data";
import { useMenuData } from "./menu-data.context";
import { CartProduct, DeliveryOptions } from "../data/types";
import { PromoCode, PromoCodeType } from "../api/api";

const deliveryPriceMapper = {
  [DeliveryOptions.SELF]: 0,
  [DeliveryOptions.COURIER]: 9.99,
};

// Application version - increment this when you want to clear old cart data
const APP_VERSION = "1.0.2";
const VERSION_STORAGE_KEY = "app_version";
const PRODUCTS_STORAGE_KEY = "products";

interface CartContextProps {
  products: CartProduct[];
  productsCount: number;
  productsPrice: number;
  discountedProductsPrice: number;
  deliveryPrice: number;
  discount: number;
  discountData: PromoCode | undefined;
  summary: number;
  addProduct: (productId: string, count: number) => boolean;
  addSingleProduct: (productId: string) => boolean;
  deleteProduct: (productId: string) => boolean;
  setDeliveryType: (type: DeliveryOptions) => void;
  setPromoCode: (code?: PromoCode) => void;
  clearCart: () => void;
  setCartRef: (ref: HTMLElement | null) => void;
  cartRef: MutableRefObject<HTMLElement | null>;
}

const getSavedVersion = (): string | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem(VERSION_STORAGE_KEY);
};

const saveVersion = (version: string): void => {
  if (typeof window === "undefined") {
    return;
  }
  localStorage.setItem(VERSION_STORAGE_KEY, version);
};

const compareVersions = (
  savedVersion: string,
  currentVersion: string,
): boolean => {
  // Simple version comparison - returns true if savedVersion is older than currentVersion
  // This works for semantic versioning (e.g., "1.0.0" < "1.0.1" < "2.0.0")
  const savedParts = savedVersion.split(".").map(Number);
  const currentParts = currentVersion.split(".").map(Number);

  for (let i = 0; i < Math.max(savedParts.length, currentParts.length); i++) {
    const savedPart = savedParts[i] || 0;
    const currentPart = currentParts[i] || 0;

    if (savedPart < currentPart) {
      return true; // savedVersion is older
    }
    if (savedPart > currentPart) {
      return false; // savedVersion is newer (shouldn't happen, but handle it)
    }
  }

  return false; // versions are equal
};

const shouldClearCart = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  const savedVersion = getSavedVersion();

  // If no version is saved, clear the cart
  if (!savedVersion) {
    return true;
  }

  // If saved version is older than current version, clear the cart
  if (compareVersions(savedVersion, APP_VERSION)) {
    return true;
  }

  // Versions match or saved version is newer, don't clear
  return false;
};

const addProductsToLocalStorage = (products: CartProduct[]) => {
  if (typeof window === "undefined") {
    return;
  }

  localStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify(products));
  // Save current version when saving products
  saveVersion(APP_VERSION);
};

const getProductsFromLocalStorage = (): CartProduct[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const products = localStorage.getItem(PRODUCTS_STORAGE_KEY);

  return products ? JSON.parse(products) : [];
};

const CartContext = createContext<CartContextProps | null>(null);

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
};

const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { products: menuProducts, restaurantProducts } = useMenuData();
  const [products, setProducts] = useState<CartProduct[]>([]);
  const [deliveryPrice, setDeliveryPrice] = useState(
    deliveryPriceMapper[DeliveryOptions.COURIER],
  );
  const [discountData, setDiscountData] = useState<PromoCode>();

  const clearCart = useCallback(() => {
    setProducts([]);
    addProductsToLocalStorage([]);
  }, [setProducts]);

  const cartRef = useRef<HTMLElement | null>(null);

  const setCartRef = useCallback((ref: HTMLElement | null) => {
    cartRef.current = ref;
  }, []);

  const addProduct = useMemo(
    () => (productId: string, count: number) => {
      if (productId == null) {
        return false;
      }

      const productIdx = products.findIndex(
        (item: CartProduct) => item.productId === productId,
      );

      if (productIdx >= 0) {
        products[productIdx].count = count;

        setProducts([...products]);
        addProductsToLocalStorage(products);
        return true;
      }

      const uniqueProducts = [...new Set([...products, { productId, count }])];

      setProducts(uniqueProducts);
      addProductsToLocalStorage(uniqueProducts);
      return true;
    },
    [products],
  );

  const addSingleProduct = useMemo(
    () => (productId: string) => {
      if (!productId) {
        return false;
      }

      const productIdx = products.findIndex(
        (item: CartProduct) => item.productId === productId,
      );

      if (productIdx >= 0) {
        products[productIdx].count += 1;

        setProducts([...products]);
        addProductsToLocalStorage(products);
        return true;
      }

      const newProducts = [...products, { productId, count: 1 }];

      setProducts(newProducts);
      addProductsToLocalStorage(newProducts);
      return true;
    },
    [products],
  );

  const deleteProduct = useMemo(
    () => (productId: string) => {
      const idx = products.findIndex((item) => item.productId === productId);

      if (idx >= 0) {
        products.splice(idx, 1);
        setProducts((curr) => [...products]);
        addProductsToLocalStorage([...products]);
      }

      return false;
    },
    [products],
  );

  const productsPrice = useMemo(() => {
    let price = 0;

    products.forEach((item) => {
      const product = getProductById(
        menuProducts,
        restaurantProducts,
        item.productId,
      );

      if (product) {
        price += product.price * item.count;
      }
    });

    return parseFloat(price.toFixed(2));
  }, [products, restaurantProducts]);

  const discount = useMemo(() => {
    let discountValue = 0;

    if (!discountData) {
      return parseFloat(discountValue.toFixed(2));
    }

    switch (discountData.type) {
      case PromoCodeType.FIXED:
        discountValue = discountData.value;
        break;
      case PromoCodeType.PERCENTAGE:
        discountValue = (productsPrice * discountData.value) / 100;
        break;
    }

    return parseFloat(discountValue.toFixed(2));
  }, [discountData, productsPrice]);

  const discountedProductsPrice = useMemo(() => {
    return parseFloat((productsPrice - discount).toFixed(2));
  }, [productsPrice, discount]);

  const summary = useMemo(() => {
    const totalPrice = productsPrice + deliveryPrice - discount;

    return parseFloat(totalPrice.toFixed(2));
  }, [productsPrice, deliveryPrice, discount]);

  const productsCount = useMemo(() => {
    return products.reduce((sum, curr) => {
      sum += curr.count;
      return sum;
    }, 0);
  }, [products]);

  const setDeliveryType = useCallback(
    (type: DeliveryOptions) => {
      setDeliveryPrice(deliveryPriceMapper[type]);
    },
    [setDeliveryPrice],
  );

  const setPromoCode = useCallback(
    (code?: PromoCode) => {
      if (!code) {
        setDiscountData(undefined);
        return;
      }

      if (code.type === PromoCodeType.FIXED) {
        setDiscountData(code);
        return;
      }

      const newCode: PromoCode = { ...code, value: code.value % 100 };

      setDiscountData(newCode);
    },
    [setDiscountData],
  );

  useEffect(() => {
    if (shouldClearCart()) {
      clearCart();
      saveVersion(APP_VERSION);
    } else {
      const localStorageProducts = getProductsFromLocalStorage();
      setProducts(localStorageProducts);
    }
  }, [clearCart]);

  useEffect(() => {
    if (!menuProducts.length && !restaurantProducts.length) return;

    const allIds = new Set([
      ...menuProducts.map((p) => p.id),
      ...restaurantProducts.map((p) => p.id),
    ]);

    setProducts((current) => {
      const synced = current.filter((item) => allIds.has(item.productId));
      if (synced.length !== current.length) {
        addProductsToLocalStorage(synced);
      }
      return synced;
    });
  }, [menuProducts, restaurantProducts, setProducts]);

  return (
    <CartContext.Provider
      value={{
        products,
        productsPrice,
        deliveryPrice,
        discount,
        summary,
        addProduct,
        deleteProduct,
        addSingleProduct,
        productsCount,
        setDeliveryType,
        clearCart,
        discountData,
        setPromoCode,
        discountedProductsPrice,
        setCartRef,
        cartRef,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
